import { useState } from "react";
import { LifeBuoy, UploadCloud, CheckCircle2 } from "lucide-react";
import { API, errMsg } from "../../lib/api";
import { Field, ErrBox } from "../../components/common";

const DOC_CATEGORIES = ["Bank statements", "Merchant statements", "Previous tax returns", "TPT notices",
  "IRS notices", "Accounting reports", "POS reports", "Other supporting documents"];

export default function TaxRescue() {
  const [form, setForm] = useState({ business_name: "", owner_name: "", email: "", phone: "", ein: "",
    tpt_number: "", months_behind: "", monthly_revenue: "", tax_types: [], notes: "" });
  const [files, setFiles] = useState([{ file: null, category: DOC_CATEGORIES[0] }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleTax = (t) => setForm({ ...form, tax_types: form.tax_types.includes(t) ? form.tax_types.filter((x) => x !== t) : [...form.tax_types, t] });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      Object.entries({ ...form, tax_types: form.tax_types.join(",") }).forEach(([k, v]) => fd.append(k, v ?? ""));
      let idx = 0;
      for (const f of files) {
        if (f.file) { fd.append(`doc_${idx}`, f.file); fd.append(`cat_doc_${idx}`, f.category); idx++; }
      }
      const { data } = await API.post("/public/rescue", fd);
      setDone(data);
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
        <h1 className="mt-4 text-3xl font-bold text-navy">Your Tax Rescue Case Is Open</h1>
        <div className="money mt-4 inline-block rounded-md border border-gold/50 bg-gold-50 px-6 py-3 text-2xl font-bold text-navy" data-testid="rescue-case-number">{done.case_number}</div>
        <p className="mt-4 text-slate-600">{done.message}</p>
        <p className="mt-2 text-sm text-slate-500">A confirmation has been sent to your email. A tax professional will review your case and contact you.</p>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-slate-200 bg-white py-14">
        <div className="mx-auto flex max-w-7xl items-center gap-5 px-4 sm:px-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-md bg-copper text-white"><LifeBuoy className="h-7 w-7" /></div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-copper">AZ Tax Rescue</div>
            <h1 className="text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">Behind on business taxes?<br className="hidden sm:block" /> We help organize the mess.</h1>
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <p className="text-slate-600">Tell us where things stand. We reconstruct missing periods from your records, estimate your exposure, and build a plan to get current with the Arizona Department of Revenue and the IRS.</p>
        <form onSubmit={submit} className="card-gov mt-8 space-y-5 p-6" data-testid="rescue-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Business Name *"><input className="input-gov" value={form.business_name} onChange={set("business_name")} required data-testid="rescue-business-name" /></Field>
            <Field label="Owner Name *"><input className="input-gov" value={form.owner_name} onChange={set("owner_name")} required data-testid="rescue-owner-name" /></Field>
            <Field label="Email *"><input type="email" className="input-gov" value={form.email} onChange={set("email")} required data-testid="rescue-email" /></Field>
            <Field label="Phone"><input className="input-gov" value={form.phone} onChange={set("phone")} data-testid="rescue-phone" /></Field>
            <Field label="EIN"><input className="input-gov" placeholder="12-3456789" value={form.ein} onChange={set("ein")} data-testid="rescue-ein" hint="Stored securely and masked (e.g. **-***6789)" /></Field>
            <Field label="Arizona TPT Number"><input className="input-gov" value={form.tpt_number} onChange={set("tpt_number")} data-testid="rescue-tpt" /></Field>
            <Field label="Approx. Months Behind"><input type="number" min="0" className="input-gov" value={form.months_behind} onChange={set("months_behind")} data-testid="rescue-months-behind" /></Field>
            <Field label="Estimated Monthly Revenue"><input className="input-gov money" placeholder="45000" value={form.monthly_revenue} onChange={set("monthly_revenue")} data-testid="rescue-revenue" /></Field>
          </div>
          <div>
            <div className="label-gov">Tax Types Owed</div>
            <div className="flex flex-wrap gap-2">
              {["AZ TPT", "Federal", "Payroll", "Withholding", "Not sure"].map((t) => (
                <button type="button" key={t} onClick={() => toggleTax(t)} data-testid={`rescue-taxtype-${t.toLowerCase().replace(/ /g, "-")}`}
                  className={`rounded-full border px-3 py-1 text-sm font-semibold ${form.tax_types.includes(t) ? "border-navy bg-navy text-white" : "border-slate-300 text-slate-600"}`}>{t}</button>
              ))}
            </div>
          </div>
          <Field label="Notes"><textarea className="input-gov" rows={3} value={form.notes} onChange={set("notes")} placeholder="What happened? Any notices received?" data-testid="rescue-notes" /></Field>

          <div>
            <div className="label-gov">Supporting Documents (optional, secure upload)</div>
            <div className="space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-slate-300 p-3">
                  <UploadCloud className="h-4 w-4 text-slate-400" />
                  <input type="file" onChange={(e) => setFiles(files.map((x, j) => j === i ? { ...x, file: e.target.files[0] } : x))}
                    className="text-xs text-slate-600" data-testid={`rescue-file-${i}`} />
                  <select value={f.category} onChange={(e) => setFiles(files.map((x, j) => j === i ? { ...x, category: e.target.value } : x))}
                    className="rounded border border-slate-300 px-2 py-1 text-xs" data-testid={`rescue-file-cat-${i}`}>
                    {DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  {files.length > 1 && <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-xs text-rose-600">Remove</button>}
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setFiles([...files, { file: null, category: DOC_CATEGORIES[0] }])}
              className="mt-2 text-sm font-semibold text-navy hover:underline" data-testid="rescue-add-file">+ Add another document</button>
          </div>

          <ErrBox msg={err} />
          <button className="btn-copper w-full py-3 text-base" disabled={busy} data-testid="rescue-submit">
            {busy ? "Opening your case…" : "Open My Tax Rescue Case"}
          </button>
          <p className="text-center text-xs text-slate-400">Your information is encrypted in transit, stored securely, and reviewed only by authorized tax staff. Identifiers are masked in our systems.</p>
          <p className="text-center text-xs text-slate-500">Looking for official relief options first? See our <a href="/resources" className="font-semibold text-navy underline">Arizona Tax Relief Resources</a> — payment plans, penalty abatement, and voluntary disclosure links.</p>
        </form>
      </section>
    </div>
  );
}
